import type { FC, ReactNode } from "react";
import { cn } from "../../lib/utils";
import { ClickableIcon } from "../../primitives/ClickableIcon";
import { TruncatedLabel } from "../../ui/TruncatedLabel";
import { trackingAttrs } from "../../utils";

export type FileRowProps = {
	/** Display name, already including any extension. */
	name: string;
	/** Single status indicator rendered after the name. Reach for `actions` for row controls. */
	status?: ReactNode;
	/** Row controls (preview, download, ...) rendered between the status and the remove affordance. */
	actions?: ReactNode;
	/** Renders the name in the danger tone. */
	danger?: boolean;
	/** Omit to render the row without a remove affordance. */
	onRemove?: () => void;
	removeAriaLabel?: string;
	/** Surfaced as `data-status` so hosts can assert on row state. */
	dataStatus?: string;
	muted?: boolean;
	className?: string;
	testId?: string;
	trackingId?: string;
};

/**
 * The condensed uploaded-file row: one line, name + status + actions + remove. Use instead of a
 * full file card when the file list is secondary to the upload affordance above it.
 */
export const FileRow: FC<FileRowProps> = ({
	name,
	status,
	actions,
	danger = false,
	onRemove,
	removeAriaLabel = "Remove file",
	dataStatus,
	muted = false,
	className,
	testId = "file-row",
	trackingId,
}) => (
	<div
		{...trackingAttrs(trackingId)}
		data-testid={testId}
		data-status={dataStatus}
		className={cn(
			"flex items-center gap-3 rounded-md border border-bc-strong bg-surface-base px-3 py-2.5 transition-opacity duration-200",
			muted && "opacity-60",
			className,
		)}
	>
		<TruncatedLabel
			label={name}
			className={cn(
				"min-w-0 flex-1 text-[14px] leading-[20px]",
				danger ? "text-danger" : "text-secondary",
			)}
		/>

		<div className="flex shrink-0 items-center gap-1.5">
			{status && (
				<span
					data-testid={`${testId}-status`}
					className="inline-flex h-6 min-w-6 shrink-0 items-center justify-center"
				>
					{status}
				</span>
			)}
			{actions && (
				<div
					data-testid={`${testId}-actions`}
					className="flex shrink-0 items-center empty:hidden gap-0.5"
				>
					{actions}
				</div>
			)}
			{onRemove && (
				<ClickableIcon
					type="trash"
					size="mini"
					color="danger"
					aria-label={removeAriaLabel}
					className="rounded p-1 transition-colors hover:bg-surface-neutral"
					onClick={onRemove}
					trackingId={trackingId ? `${trackingId}-remove` : undefined}
				/>
			)}
		</div>
	</div>
);
